import { useEffect, useState } from "react";
import { Radio, TrendingUp, RefreshCw, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const LEAGUES = ["NBA", "NHL", "NFL", "MLB"];

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function americanOdds(price) {
  if (price == null) return "—";
  const n = Number(price);
  return n > 0 ? `+${n}` : `${n}`;
}

// pick the first bookmaker that has data for a given market as the "headline" line
function headlineLine(game, market) {
  for (const b of game.books || []) {
    if (b[market] && b[market].length) return { book: b.book, outcomes: b[market] };
  }
  return null;
}

function GameCard({ game }) {
  const [open, setOpen] = useState(false);
  const ml = headlineLine(game, "h2h");
  const spread = headlineLine(game, "spreads");
  const total = headlineLine(game, "totals");
  const totalOver = total?.outcomes?.find((o) => (o.name || "").toLowerCase() === "over");

  return (
    <div data-testid={`live-game-${game.id}`}
      className="rounded-xl bg-[#0F1420] border border-[#232D42] overflow-hidden hover:border-[#2A3550] transition-colors">
      <div className="px-4 py-3 flex items-center justify-between border-b border-[#232D42]/70">
        <div className="flex items-center gap-2">
          <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono">{game.league}</Badge>
          <span className="text-xs text-slate-500 font-mono flex items-center gap-1">
            <Clock size={11} /> {fmtTime(game.commence_time)}
          </span>
        </div>
        <span className="text-[10px] font-mono text-slate-500">{game.book_count} books</span>
      </div>

      <div className="px-4 py-3">
        <div className="flex items-baseline justify-between mb-1">
          <span className="font-heading font-semibold text-slate-100">{game.away_team}</span>
          <span className="text-xs text-slate-500">@</span>
          <span className="font-heading font-semibold text-slate-100">{game.home_team}</span>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-lg bg-[#151B2B] border border-[#232D42] px-2 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Moneyline</div>
            {ml ? ml.outcomes.map((o) => (
              <div key={o.name} className="text-xs text-slate-200 flex justify-between">
                <span className="truncate mr-1">{o.name.split(" ").pop()}</span>
                <span className="font-mono text-[#00E5A3]">{americanOdds(o.price)}</span>
              </div>
            )) : <span className="text-xs text-slate-600">—</span>}
          </div>
          <div className="rounded-lg bg-[#151B2B] border border-[#232D42] px-2 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Spread</div>
            {spread ? spread.outcomes.map((o) => (
              <div key={o.name} className="text-xs text-slate-200 flex justify-between">
                <span className="truncate mr-1">{o.name.split(" ").pop()}</span>
                <span className="font-mono text-slate-300">{o.point > 0 ? `+${o.point}` : o.point}</span>
              </div>
            )) : <span className="text-xs text-slate-600">—</span>}
          </div>
          <div className="rounded-lg bg-[#151B2B] border border-[#232D42] px-2 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Total</div>
            {totalOver ? (
              <div className="text-xs text-slate-200">
                <span className="font-mono text-slate-300">O/U {totalOver.point}</span>
              </div>
            ) : <span className="text-xs text-slate-600">—</span>}
          </div>
        </div>

        <button data-testid={`live-game-books-toggle-${game.id}`} onClick={() => setOpen((v) => !v)}
          className="mt-3 text-[11px] text-slate-400 hover:text-[#00E5A3] font-mono">
          {open ? "Hide" : "Compare"} {game.book_count} sportsbooks {open ? "▲" : "▼"}
        </button>

        {open && (
          <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
            {(game.books || []).map((b) => {
              const h2h = b.h2h || [];
              return (
                <div key={b.key} className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-[#0B0F18]">
                  <span className="text-slate-400">{b.book}</span>
                  <span className="font-mono text-slate-300 flex gap-2">
                    {h2h.map((o) => (
                      <span key={o.name}>{o.name.split(" ").pop()} {americanOdds(o.price)}</span>
                    ))}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function LiveLines() {
  const [league, setLeague] = useState("NBA");
  const [games, setGames] = useState([]);
  const [status, setStatus] = useState(null);
  const [injuries, setInjuries] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [o, s, i] = await Promise.all([
        api.get(`/live/odds?league=${league}`),
        api.get("/live/status"),
        api.get(`/live/injuries?league=${league}`),
      ]);
      setGames(o.data.games || []);
      setStatus(s.data);
      setInjuries(i.data);
    } catch { /* keep last data on transient errors */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league]);

  const updatedAt = status?.status?.updated_at;
  const remaining = status?.status?.odds_quota_remaining;

  return (
    <div data-testid="live-lines-page" className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E5A3] opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E5A3]" />
          </span>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 flex items-center gap-2">
            <Radio size={22} className="text-[#00E5A3]" /> Live Lines
          </h1>
          <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono ml-1">REAL DATA</Badge>
        </div>
        <button data-testid="live-refresh-btn" onClick={load}
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#00E5A3] font-mono">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="text-sm text-slate-400 mb-4 max-w-2xl">
        Genuinely live upcoming games and real sportsbook lines across US books —
        pulled straight from the market, not simulated.
        {updatedAt && <span className="text-slate-600 font-mono"> · updated {fmtTime(updatedAt)}</span>}
      </p>

      <div className="flex items-center gap-2 mb-5">
        {LEAGUES.map((lg) => (
          <button key={lg} data-testid={`live-league-${lg}`} onClick={() => setLeague(lg)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              league === lg ? "bg-[#00E5A3] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
            {lg}
          </button>
        ))}
      </div>

      {/* Real ESPN injuries/status for this league */}
      {injuries && injuries.injuries?.length > 0 && (
        <div className="mb-5 rounded-xl border border-[#232D42] bg-[#0F1420] p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={15} className="text-[#00E5A3]" />
            <span className="font-heading font-semibold text-slate-100">Injuries & Status · {league}</span>
            <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono">REAL · ESPN</Badge>
            <span className="ml-auto text-[10px] font-mono text-slate-600">{injuries.count} listed</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {injuries.injuries.slice(0, 12).map((inj) => (
              <a key={inj.id} href={`/players/${inj.id}`} data-testid={`live-injury-${inj.id}`}
                className="text-xs px-3 py-2 rounded-lg bg-[#151B2B] border border-[#232D42] hover:border-[#2A3550] block">
                <div className="flex justify-between items-center">
                  <span className="text-slate-200 font-medium">{inj.name}
                    <span className="text-slate-500 font-mono ml-1">{inj.team_abbr} · {inj.position}</span>
                  </span>
                  <span className="text-[#FF6B6B] font-mono uppercase text-[10px]">{inj.status}</span>
                </div>
                {inj.status_note && <p className="text-slate-500 mt-0.5 truncate">{inj.status_note}</p>}
              </a>
            ))}
          </div>
        </div>
      )}

      {loading && games.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading live lines…</div>
      ) : games.length === 0 ? (
        <div data-testid="live-empty" className="text-center py-16 text-slate-500 text-sm">
          No {league} games on the board right now. Lines appear as sportsbooks post them for upcoming games.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {games.map((g) => <GameCard key={g.id} game={g} />)}
        </div>
      )}

      {remaining != null && (
        <p className="text-[10px] text-slate-700 font-mono mt-6 text-center">
          odds quota remaining this month: {remaining}
        </p>
      )}
    </div>
  );
}
